import React from "react";
import { useIntl } from "react-intl";

import styled from "styled-components";

import Attachments from "@components/Attachments";
import { PrimaryButton, PrimaryButtonOutlined } from "@components/Button";
import Card from "@components/Card";
import DataTable from "@components/DataTableV2/DataTable";
import Div from "@components/Div";
import ProgressSpinner from "@components/ProgressSpinner";
import PromptDialog from "@components/PromptDialog";
import TabHeader from "@components/TabHeader";
import { TextLargeSemiBoldWeight } from "@components/Text";

import { AddCrimesDialog, EditCrimesDialog, RawDataViewer } from "./components";

import { useCrimesDialog } from "./CrimesTabAlternative.hooks";

const StyledDataTableWrapper = styled(Div)`
  .p-datatable.p-datatable-custom {
    .p-datatable-wrapper {
      overflow-x: hidden !important;

      @media (max-width: 1200px) {
        overflow-x: auto !important;
      }
    }
  }
`;

const CrimesDialog = () => {
  const { formatMessage, messages } = useIntl();
  const {
    config,
    shouldShowAddCrimeDialog,
    isCrimesEditable,
    selectedDeleteData,
    selectedEditData,
    shouldShowRawDataViewer,
    sourceData,
    attachments,
    isLoading,
    isDataChanged,
    tabHeader,
    handleSave,
    handleCompleteCrimesData,
    handleAddCrime,
    handleCloseAddCrimeDialog,
    handleEditCrime,
    handleCloseEditCrimeDialog,
    handleDeleteCrime,
    handleCloseDeleteCrimeDialog,
  } = useCrimesDialog();

  const renderAttachments = () => (
    <Card
      header={
        <Div p={3}>
          <TextLargeSemiBoldWeight>
            {messages.title_documents_from_court}
          </TextLargeSemiBoldWeight>
        </Div>
      }
    >
      <Div px={3} pb={3}>
        <Div border="1px solid var(--grey-lightest)" width={1} mb={3} />
        <Attachments attachments={attachments} />
      </Div>
    </Card>
  );

  return (
    <>
      {isLoading && <ProgressSpinner />}

      {shouldShowAddCrimeDialog && (
        <AddCrimesDialog
          sourceDataType={sourceData?.type}
          onHide={handleCloseAddCrimeDialog}
          onAdd={handleAddCrime}
        />
      )}

      {selectedEditData && (
        <EditCrimesDialog
          sourceDataType={sourceData?.type}
          crimeDetails={selectedEditData}
          onHide={handleCloseEditCrimeDialog}
          onEdit={handleEditCrime}
        />
      )}

      {selectedDeleteData && (
        <PromptDialog
          title={messages.label_delete_crime}
          message={`${formatMessage({ id: "message_delete_crime" })} ${selectedDeleteData?.crime_name}`}
          onConfirm={handleDeleteCrime}
          onCancel={handleCloseDeleteCrimeDialog}
          confirmLabel={messages.label_delete}
        />
      )}

      {isCrimesEditable && <TabHeader {...tabHeader} />}

      <StyledDataTableWrapper width="100%">
        <DataTable config={config} resetData={!isLoading} />
      </StyledDataTableWrapper>

      {!!attachments?.length && renderAttachments()}

      {isDataChanged && (
        <Div
          display="flex"
          flexDirection={["column", "column", "row", "row"]}
          gridGap={4}
        >
          <PrimaryButton
            label={messages.label_save_and_complete_source_data}
            onClick={handleCompleteCrimesData}
          />
          <PrimaryButtonOutlined
            label={messages.label_save}
            px={4}
            onClick={handleSave}
          />
        </Div>
      )}

      {shouldShowRawDataViewer && <RawDataViewer sourceData={sourceData} />}
    </>
  );
};

export default CrimesDialog;
